import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { chooseGuestRescheduleMeeting, getGuestRescheduleOptions } from '../lib/meetings';

function label(startsAt) {
  return new Date(startsAt).toLocaleString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/**
 * Reached from the cancellation email. This is the page that stops "we cancelled
 * your meeting" ending at "please contact Leadership": the guest picks another
 * morning themselves, and anything they've paid comes with them.
 */
export default function GuestReschedule() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [state, setState] = useState({ status: 'loading' });
  const [selected, setSelected] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setState({ status: 'invalid' });
      return;
    }
    let cancelled = false;
    getGuestRescheduleOptions(token)
      .then((data) => {
        if (cancelled) return;
        setState({ status: 'ready', data });
        setSelected(data?.meetings?.[0]?.id ?? '');
      })
      .catch(() => { if (!cancelled) setState({ status: 'invalid' }); });
    return () => { cancelled = true; };
  }, [token]);

  const onConfirm = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setError('');
    setIsSaving(true);
    try {
      const result = await chooseGuestRescheduleMeeting(token, selected);
      setState((prev) => ({ status: 'done', data: prev.data, result }));
    } catch (err) {
      setError(err?.message ?? 'We could not move your registration. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (state.status === 'loading') {
    return (
      <div className="max-w-2xl mx-auto py-10 px-6">
        <p className="text-gray-600">Loading…</p>
      </div>
    );
  }

  if (state.status === 'invalid') {
    return (
      <div className="max-w-2xl mx-auto py-10 px-6" data-testid="reschedule-invalid">
        <h1 className="text-2xl font-semibold text-NBNA-primary">Choose another meeting</h1>
        <p className="mt-3 text-gray-800">
          This link has expired. Please reply to the email we sent you and we&apos;ll sort it out.
        </p>
      </div>
    );
  }

  if (state.status === 'done') {
    return (
      <div className="max-w-2xl mx-auto py-10 px-6" data-testid="reschedule-done">
        <h1 className="text-2xl font-semibold text-NBNA-primary">You&apos;re booked</h1>
        <p className="mt-3 text-gray-800">
          We&apos;ve moved your registration to {label(state.result.startsAt)}.
        </p>
        {state.result.paid ? (
          <p className="mt-2 text-gray-700">
            Your guest fee carries over, so there&apos;s nothing further to pay.
          </p>
        ) : null}
        <p className="mt-2 text-gray-700">Bring business cards — see you there.</p>
      </div>
    );
  }

  const { data } = state;

  return (
    <div className="max-w-2xl mx-auto py-10 px-6" data-testid="reschedule-form">
      <h1 className="text-2xl font-semibold text-NBNA-primary">Choose another meeting</h1>

      {data.current ? (
        <p className="mt-3 text-gray-800">
          You&apos;re currently registered for {label(data.current.startsAt)}.
          {data.current.paid ? ' Your guest fee is paid and moves with you.' : ''}
        </p>
      ) : (
        <p className="mt-3 text-gray-800">Pick the morning that suits you.</p>
      )}

      {data.meetings?.length ? (
        <form onSubmit={onConfirm} className="mt-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="reschedule-meeting">
              Meeting
            </label>
            <select
              id="reschedule-meeting"
              data-testid="reschedule-meeting"
              className="input-field w-full"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              {data.meetings.map((m) => (
                <option key={m.id} value={m.id}>{label(m.startsAt)}</option>
              ))}
            </select>
          </div>

          {error ? <p className="text-sm text-red-700">{error}</p> : null}

          <button
            type="submit"
            className="btn-primary disabled:opacity-40"
            data-testid="reschedule-confirm"
            disabled={isSaving || !selected}
          >
            {isSaving ? 'Moving…' : 'Move my registration'}
          </button>
        </form>
      ) : (
        <p className="mt-4 text-gray-700">
          There are no meetings on the calendar just yet. We&apos;ll email you as soon as the next
          one is scheduled — this link will still work.
        </p>
      )}
    </div>
  );
}
